import { forwardRef, useMemo } from 'react';

import { SizingNames } from '@spazious/dollhouse-theme';
import { styled } from '@spazious/dollhouse-theme';

import { Flex, FlexElement, FlexProps, FlexPropsList } from '../Flex/index.js';

import { FieldContext } from './FieldContext.js';

export type FieldElement = FlexElement;

/**
 * Is a component intended to be used as a form field wrapper of a user input element.
 *
 * It has a responsive layout and can be used to wrap any form element.
 *
 * @example
 *
 * import { Field, FieldProps, Select } from '@spazious/dollhouse-uikit';
 *
 * ```tsx
 *
 * interface MyComponentProps extends FieldProps {
 *    // Your props
 * }
 *
 * const MyComponent = (props) => {
 *   return (
 *     <Field {...props}>
 *       <Field.Label>Select component</Field.Label>
 *       <Select>
 *         <Select.Option value="1">
 *           Option 1
 *         </Select.Option>
 *       </Select>
 *     </Field>
 *   );
 * };
 * ```
 *
 */
export interface FieldProps extends FlexProps {
  /**
   * The label of the field
   */
  label?: string;
  /**
   * The size of the field
   */
  size?: SizingNames;
  /**
   * The error flag of the field
   */
  error?: boolean;
  /**
   * The title of the field
   */
  title?: string;
  /**
   * The required flag of the field
   */
  required?: boolean;
  /**
   * The disabled flag of the field
   */
  disabled?: boolean;
}

export const FieldPropsList: Array<keyof FieldProps> = [
  ...FlexPropsList,
  'label',
  'size',
  'error',
  'title',
  'required',
  'disabled',
];

export const Field = styled(
  forwardRef<HTMLDivElement, FieldProps>(
    ({ children, disabled, column = true, error, size = 'medium', label, ...restProps }, ref) => {
      const ctxValue = useMemo(
        () => ({ disabled, error: !!error, size, label }),
        [size, disabled, error, label]
      );
      return (
        <Flex
          ref={ref}
          alignItems={column ? 'stretch' : 'center'}
          column={column}
          gap="var(--spacing-gap)"
          {...restProps}
        >
          <FieldContext.Provider value={ctxValue}>{children}</FieldContext.Provider>
        </Flex>
      );
    }
  )
).withConfig({
  displayName: 'Field',
})``;
